/**
 * OSMDExerciseScene - the Exercise Section's playable scene (MUS-317, "7b").
 *
 * A leaf on the shared OSMDPlayableScene base (transport / cursor / accuracy via
 * OSMDEventStream). It binds the session state machine (MUS-315) to that
 * machinery: it provides the ten side-effect actions the machine commands, drives
 * the session lifecycle, and renders the exercise notation the expander produces.
 *
 * Reuse (per the MUS-317 spike): the OSMD render derives its own cursor/note-graph
 * from the rendered score (OSMDIteratorExtensible reads note positions off the
 * render, not a server-side cursor), so the exercise feeds a light, client-built
 * `Phrase` - real MusicXML from the expander, empty cursor arrays - straight
 * through the base's `addAllPhrases`. No backend artifacts, no adapter.
 *
 * Grading: the notes resolve on the shared event stream, exactly as on Sight Reading
 * and Repertoire. A note never played turns red when its window closes (the stream's
 * missed-note sweep runs every frame while the transport runs); each hand's last note
 * is graded on its onset only. The take ends once every note's window has closed, and
 * its tally is read from those same resolved notes, so the panel always matches the
 * colours. The take record is written beside the grade, never in place of it: a
 * record that cannot be written is reported and the take is graded all the same.
 *
 * Everything is reached only behind the USE_EXERCISES flag.
 */

import { createActor, type Actor } from 'xstate';
import MusicXML from 'Models/MusicXML';
import Phrase from 'Models/Phrase';
import TimeSignature from 'Models/TimeSignature';
import Logger from 'Utils/Logger';
import { reportError } from 'Utils/reportError';
import { v4 as uuidv4 } from 'uuid';
import OSMDPlayableScene from './OSMDPlayableScene';
import {
  exerciseSessionMachine,
  type SideEffectActionName,
} from 'Phaser/ExerciseSessionMachine/exerciseSessionMachine';
import type {
  ExerciseSessionContext,
  ExerciseSessionEmitted,
  SessionExercise,
} from 'Phaser/ExerciseSessionMachine/exerciseSessionMachine.types';
import type { ScaleCell, ScaleConfig } from 'Types/ExerciseContentTypes';
import type { StepwiseRunPrompt } from 'Types/ExerciseRecipeTypes';
import type { OSMDEventStream } from 'Phaser/GameObjects/OSMDEventStream';
import type { OSMDPhraseDataEventPayload } from 'Phaser/EventBus2/ToPhaser';
import type { PrerenderedPhraseData } from 'Types';
import { expandStepwiseRun, type JudgedNote } from './OSMDExerciseScene.expander';
import type { FingerNumbersDisplay } from 'Seeds/ExerciseContent/scaleFingering';
import { RUN_METER } from './OSMDExerciseScene.runScore';
import type { KeySignatureDisplay } from 'Seeds/ExerciseContent/scaleNotation';
import { gradeNoteStates, type GradeComputation } from './OSMDExerciseScene.grader';
import {
  anyKeyPressed,
  exerciseScore,
  recordExerciseTake,
  startExerciseRecord,
  type ExerciseMaterialKey,
} from './OSMDExerciseScene.takeRecord';
import {
  linkPlaySession,
  type PerformanceEvent,
  type RecorderState,
  type SessionRecord,
  type TakeEndReason,
} from 'Grading';
import type { MidiOffPayload, MidiOnPayload } from 'Phaser/EventBus2/ToPhaser';
import { takeKey } from 'Phaser/ExerciseSessionMachine/sessionTraversal';

/** DIVISIONS=4 per quarter in the expander's MusicXML space, so 16 per whole note. */
const WHOLE_NOTE_DIVISIONS = 16;

/**
 * Build the Phrase model's cursor data from the run (F1).
 *
 * `Models/Phrase` computes its own pickup/end timing from `cursorData` in the
 * constructor (`calcPickUpLength` / `calcEndTimestamp` dereference the last datum
 * unconditionally), so an empty array throws and zero-timing is wrong. The OSMD
 * *cursor* is still re-derived from the render at play time; this only supplies the
 * model's timing/layout metadata, in whole-note units, from the same judged run.
 */
const judgedNotesToCursorData = (
  judgedNotes: JudgedNote[],
  [beats, beatType]: [number, number],
): PrerenderedPhraseData[] => {
  const measureDivisions = (beats / beatType) * WHOLE_NOTE_DIVISIONS;
  const byOnset = new Map<number, JudgedNote[]>();
  for (const note of judgedNotes) {
    const at = byOnset.get(note.startDivisions) ?? [];
    at.push(note);
    byOnset.set(note.startDivisions, at);
  }
  return [...byOnset.keys()]
    .sort((a, b) => a - b)
    .map((startDivisions) => ({
      currentTimestamp: startDivisions / WHOLE_NOTE_DIVISIONS,
      currentMeasureIndex: Math.floor(startDivisions / measureDivisions),
      currentVoiceEntries: byOnset.get(startDivisions)!.map((note) => ({
        Notes: note.midiNotes.map((midi) => ({
          fingering: undefined,
          graphics: [],
          isRest: false,
          pitch: {
            accidental: 0,
            fundamentalNote: 0,
            octave: 0,
            halfTone: midi - 12,
            frequency: 440 * Math.pow(2, (midi - 69) / 12),
          },
          noteTie: undefined,
          length: note.durationDivisions / WHOLE_NOTE_DIVISIONS,
          printObject: true,
          staffPosition: note.hand === 'RH' ? 'top' : 'bottom',
        })),
      })),
    }));
};

/**
 * User-driven lifecycle controls. These are the events a real per-session screen
 * (Phase 3) will drive; the dev launcher stands in for now. `start`/`next` open a
 * take's response window (begin the count-in). A take ends on its own when the
 * music ends (the last note's release window has closed): the transport stops and
 * the real played note stream is graded, with no Submit. `stopTake` leaves the take
 * early: it stops the transport and the take is never graded and never counts.
 */
export interface ExerciseSessionControls {
  start(): void;
  stopTake(): void;
  next(): void;
  end(): void;
}

/** Runtime input for one exercise sitting, handed to the scene at launch. */
export interface OSMDExerciseSessionInput {
  prompt: StepwiseRunPrompt;
  exercise: SessionExercise;
  cell: ScaleCell;
  config: ScaleConfig;
  /** Thin observation seam: the machine's emitted events (7c replaces this with
   *  the Redux slice + saga, MUS-318). */
  onSessionEvent?: (event: ExerciseSessionEmitted) => void;
  /** Inbound seam: receives the lifecycle controls once the actor is running. */
  onReady?: (controls: ExerciseSessionControls) => void;
  /** A take ended (graded, no input, or stopped): the screen may offer Next. */
  onTakeEnded?: () => void;
  /** Which content the run was realized from, stored with every take. */
  materialKey?: ExerciseMaterialKey | null;
  /** Read when a take opens: the metronome clicks during takes (an assist on the stamp). */
  isClickOn?: () => boolean;
  /** Read when the sitting's record starts. */
  appVersion?: () => string | null;
  /** Read when a run loads: the user's key-signature display setting. */
  keySignatureDisplay?: () => KeySignatureDisplay;
  /** Read when a run loads: whether the reference finger numbers are drawn. */
  fingerNumbers?: () => FingerNumbersDisplay;
  /** Read when a take ends: the sitting's play-session id, once the open has answered. */
  playSessionId?: () => string | null;
  /** Every ended take: the sitting's take record as it now stands, to be sent. */
  onTakeRecorded?: (record: SessionRecord) => void;
}

/** The take being played, as its key events arrive. Times are on the session clock. */
interface OpenTake {
  readonly openedAtMs: number;
  readonly events: PerformanceEvent[];
  beatZeroMs: number | null;
}

type SessionActor = Actor<typeof exerciseSessionMachine>;

/** The base `init` data plus the exercise session input. */
type OSMDExerciseInitData = {
  eventStream: OSMDEventStream;
  width: number;
  height: number;
  mobile?: boolean;
  performanceTempo?: number;
  cursorVisible?: boolean;
  zoomLevel?: number;
  exerciseSession?: OSMDExerciseSessionInput;
} & Record<string, unknown>;

export default class OSMDExerciseScene extends OSMDPlayableScene {
  private exerciseSession: OSMDExerciseSessionInput | null = null;
  private sessionActor: SessionActor | null = null;
  /** The expander's judged-note stream for the loaded run (MUS-320 grades it). */
  private judgedNotes: JudgedNote[] = [];
  /** The last take's computed grade, held for the visible signal (MUS-320). */
  private lastGrade: GradeComputation | null = null;
  /** The reused in-scene gradient + status text. NOT the Phase-3 HUD (MUS-482). */
  private gradeSignalText: Phaser.GameObjects.Text | null = null;
  /** The open take has already been ended (auto-submit fires once per take). */
  private takeEnding = false;
  /** The sitting's take record: every take is graded and recorded through it. */
  private record: RecorderState | null = null;
  private recordMaterialId: string | null = null;
  /** performance.now() at session clock 0. */
  private recordStartedAtPerfMs = 0;
  private openTake: OpenTake | null = null;
  /** How the loaded run is written (read when it loads); stored with each of its takes. */
  private runKeySignatureDisplay: KeySignatureDisplay | null = null;

  constructor() {
    super('OSMDExerciseScene');
  }

  init(data: OSMDExerciseInitData): void {
    super.init(data);
    this.exerciseSession = data.exerciseSession ?? null;
  }

  create(): void {
    super.create();
    if (this.exerciseSession) {
      this.startSession(this.exerciseSession);
    }
  }

  update(): void {
    super.update();
    this.captureBeatZero();
    this.resolveDueNotes();
    this.endTakeWhenTheMusicEnds();
  }

  /** True while a take's transport is running. */
  private transportRunning(): boolean {
    const timeKeeper = this.eventStream?.timeKeeper;
    return this.playbackStarted && !!timeKeeper && !timeKeeper.isPaused();
  }

  /**
   * The shared stream's missed-note sweep, as Sight Reading and Repertoire run it: a note
   * whose window has closed unplayed turns red now, not at the end of the take.
   */
  private resolveDueNotes(): void {
    if (!this.transportRunning()) return;
    this.eventStream?.update();
  }

  async noteOn(payload: MidiOnPayload): Promise<void> {
    this.recordKey({ kind: 'press', pitch: payload.note, velocity: payload.velocity });
    return super.noteOn(payload);
  }

  noteOff(payload: MidiOffPayload): void {
    this.recordKey({ kind: 'release', pitch: payload.note });
    super.noteOff(payload);
  }

  /** The device clock, in ms (performance.now); a seam for the specs. */
  protected deviceNowMs(): number {
    return performance.now();
  }

  /** Milliseconds on the sitting's session clock. */
  private sessionNowMs(): number {
    return this.deviceNowMs() - this.recordStartedAtPerfMs;
  }

  /** A key event during an open take, on the session clock (raw keyboard input). */
  private recordKey(
    key: { kind: 'press'; pitch: number; velocity: number } | { kind: 'release'; pitch: number },
  ): void {
    if (!this.openTake) return;
    const recorded =
      key.kind === 'press' ? { ...key, velocity: recordableVelocity(key.velocity) } : key;
    this.openTake.events.push({ ...recorded, timeMs: this.sessionNowMs() });
  }

  /**
   * Beat 0 of the take: the transport's start, which the take's first note sets off. Read
   * once the transport is running, from how far its audio clock has run since it started.
   */
  private captureBeatZero(): void {
    const take = this.openTake;
    if (!take || take.beatZeroMs !== null || !this.playbackStarted) return;
    const timeKeeper = this.eventStream?.timeKeeper;
    if (!timeKeeper || timeKeeper.isPaused()) return;
    const sinceStartMs =
      (timeKeeper.getAudioContextTime() - timeKeeper.getPlaybackStartTime()) * 1000;
    take.beatZeroMs = this.sessionNowMs() - sinceStartMs;
  }

  /** Opens a take's record when its response window opens (and the sitting's, at the first). */
  private openTakeRecord(): void {
    if (!this.record) {
      this.recordStartedAtPerfMs = this.deviceNowMs();
      const session = this.exerciseSession;
      if (!session) return;
      this.recordMaterialId = uuidv4();
      try {
        this.record = startExerciseRecord({
          sessionId: uuidv4(),
          startedAt: new Date().toISOString(),
          appVersion: session.appVersion?.() ?? null,
          materialId: this.recordMaterialId,
          score: exerciseScore(this.judgedNotes, RUN_METER, session.materialKey ?? null),
        });
      } catch (error) {
        this.record = null;
        reportTakeRecordFailure('could not start', error);
      }
    }
    this.openTake = { openedAtMs: this.sessionNowMs(), events: [], beatZeroMs: null };
  }

  /**
   * Ends the open take in the record and hands the sitting's record on to be sent. The record
   * holds the facts (the material, every key event, the stamp, the setup); the take's grade is
   * read from the stream's resolved notes, never from here. Returns whether the take was
   * recorded; a take the record cannot hold is reported, never silently dropped.
   */
  private closeTakeRecord(reason: TakeEndReason): boolean {
    const take = this.openTake;
    const session = this.exerciseSession;
    this.openTake = null;
    if (!take || !session) return false;
    if (!this.record || !this.recordMaterialId) {
      reportTakeRecordFailure('has no record to end the take in', new Error(reason));
      return false;
    }
    const config = this.sessionActor?.getSnapshot().context.currentConfig ?? session.config;
    const takeId = uuidv4();
    try {
      const recorded = recordExerciseTake(this.withPlaySessionLink(this.record), {
        takeId,
        materialId: this.recordMaterialId,
        openedAtMs: take.openedAtMs,
        beatZeroMs: take.beatZeroMs,
        events: take.events,
        endedAtMs: Math.max(this.sessionNowMs(), take.openedAtMs),
        reason,
        exerciseId: session.exercise.id,
        cell: session.cell,
        config,
        timeSignature: RUN_METER,
        clickOn: session.isClickOn?.() ?? false,
        ...(this.runKeySignatureDisplay
          ? { keySignatureDisplay: this.runKeySignatureDisplay }
          : {}),
      });
      this.record = recorded;
      session.onTakeRecorded?.(recorded.record);
      return true;
    } catch (error) {
      reportTakeRecordFailure(`could not record a ${reason} take`, error);
      return false;
    }
  }

  /** Links the record to the sitting's play session once its id is known. */
  private withPlaySessionLink(record: RecorderState): RecorderState {
    const playSessionId = this.exerciseSession?.playSessionId?.() ?? null;
    if (playSessionId === null || record.record.header.surfaceSessionId !== null) return record;
    return linkPlaySession(record, playSessionId);
  }

  /**
   * Auto-end and auto-submit: once every note's judging window has closed on the shared
   * stream (each hand's last note on its onset alone: its release is free), submit the take.
   * The machine's evaluating state then stops the transport and metronome and rules the
   * verdict, exactly as a Submit tap used to.
   */
  private endTakeWhenTheMusicEnds(): void {
    if (this.takeEnding || !this.transportRunning()) return;
    const actor = this.sessionActor;
    if (!actor?.getSnapshot().matches('playing')) return;
    if (!this.eventStream?.allWindowsClosed()) return;
    this.takeEnding = true;
    actor.send({ type: 'SUBMIT_RESPONSE', response: { kind: 'performed', payload: {} } });
  }

  /** Create the session actor, wire the emitted-event seam, and seed the sitting. */
  private startSession(input: OSMDExerciseSessionInput): void {
    const actor = createActor(exerciseSessionMachine.provide({ actions: this.sessionActions() }), {
      input: undefined,
    });
    this.sessionActor = actor;

    const forward = (event: ExerciseSessionEmitted) => input.onSessionEvent?.(event);
    actor.on('playthroughCompleted', forward);
    actor.on('pointEarned', forward);
    actor.on('sessionCompleted', forward);

    actor.start();
    actor.send({
      type: 'LOAD_SESSION',
      exercise: input.exercise,
      cell: input.cell,
      config: input.config,
    });

    input.onReady?.({
      // MUS-319: START leaves the press-start screen; RESPONSE_STARTED opens this
      // take's response window (begins the count-in via beginPlayback). The user
      // then plays real MIDI, and the take ends on its own when the music ends.
      start: () => {
        actor.send({ type: 'START' });
        actor.send({ type: 'RESPONSE_STARTED' });
      },
      // Leaving a take early: the machine stops the transport and grades nothing.
      stopTake: () => {
        actor.send({ type: 'STOP_TAKE' });
        if (!actor.getSnapshot().matches('stopped')) return;
        this.closeTakeRecord('stopped');
        this.drawStoppedSignal();
        input.onTakeEnded?.();
      },
      // NEXT advances the configuration, then RESPONSE_STARTED reopens the window
      // (a fresh count-in via initPlaythrough -> beginPlayback) for the next take.
      next: () => {
        actor.send({ type: 'NEXT' });
        actor.send({ type: 'RESPONSE_STARTED' });
      },
      end: () => actor.send({ type: 'END_SESSION' }),
    });
  }

  /** The ten side-effect commands the machine issues, bound to the base. */
  private sessionActions(): Record<
    SideEffectActionName,
    (args: { context: ExerciseSessionContext }) => void
  > {
    return {
      loadContent: () => {
        void this.loadRun();
      },
      initPlaythrough: () => {
        // MUS-319: re-arm the inherited transport for a fresh take. softReset stops
        // the base playback machine, resets every NoteContainer + the event stream,
        // and clears playbackStarted so the next beginPlayback enters a fresh
        // count-in. It supersedes the old bare eventStream.reset() (which softReset
        // itself performs), and is what lets repeated takes each start clean.
        this.softReset();
        this.takeEnding = false;
      },
      beginPlayback: () => {
        // MUS-319: open the response window by driving the inherited count-in ->
        // playing transport. This warms the native audio, clicks the count-in
        // metronome, and (critically) sets playbackStarted so the base's MIDI
        // note-on handler forwards played notes into the event stream that colors
        // the graded bands. The old bare phrase.activate() never armed that path, so
        // MIDI was dropped and the bands never moved.
        //
        // Read the tempo from the machine's live currentConfig rather than the
        // static launch input: currentConfig is what the traversal policy ramps
        // (advanceConfig -> planNextConfig), so a future non-manual policy that
        // steps the tempo per take is honored here. currentConfig is always seeded
        // (idle -> LOAD_SESSION -> loading) before this state can be reached, and
        // under the current manual policy it equals the launch tempo, so this is
        // behavior-neutral today.
        this.startExercise({
          tempo: this.sessionActor?.getSnapshot().context.currentConfig?.tempo,
          autoStart: false,
        });
        this.openTakeRecord();
      },
      stopPlayback: () => {
        // Resolve anything still due on the stream at this moment, then halt the
        // transport (metronome + scheduler + TimeKeeper). The take only auto-ends once
        // every window has closed, so every note is resolved before it is graded.
        this.eventStream?.update();
        this.pauseExercise();
      },
      gradeResponse: () => {
        this.computeGrade();
      },
      // MUS-555: render from the context XState hands the action, NOT the actor's
      // getSnapshot(). These run as state-entry actions inside the grade transition,
      // after processGradeResult has updated `earned`; the actor's snapshot is only
      // committed once the whole transition completes, so reading it here returned the
      // previous take's ledger and the status line lagged one take behind.
      showPassFeedback: ({ context }) => {
        this.renderGradeSignal(context);
        this.exerciseSession?.onTakeEnded?.();
      },
      showFailFeedback: ({ context }) => {
        this.renderGradeSignal(context);
        this.exerciseSession?.onTakeEnded?.();
      },
      pauseScene: () => {
        this.phrases.forEach((phrase) => phrase.pause());
      },
      resumeScene: () => {
        this.phrases.forEach((phrase) => phrase.activate(true));
      },
      cleanupSession: () => {
        // A take still open when the sitting ends was left: recorded, never graded.
        this.closeTakeRecord('left');
        this.record = null;
        this.recordMaterialId = null;
        this.phrases.forEach((phrase) => phrase.deactivate());
        this.judgedNotes = [];
        this.lastGrade = null;
        this.gradeSignalText?.destroy();
        this.gradeSignalText = null;
      },
    };
  }

  /** Expand the run, render it through the shared base, then answer SESSION_LOADED. */
  private async loadRun(): Promise<void> {
    const session = this.exerciseSession;
    if (!session) return;
    try {
      const keySignature = session.keySignatureDisplay?.();
      this.runKeySignatureDisplay = keySignature ?? null;
      const run = expandStepwiseRun(session.prompt, session.cell, session.config, {
        keySignature,
        fingerNumbers: session.fingerNumbers?.(),
      });
      this.judgedNotes = run.judgedNotes;
      // Each hand's last note is graded on its onset only.
      this.eventStream?.setLastNoteReleaseFree(true);
      await this.addAllPhrases([this.toPhrasePayload(run.musicXml)]);
      // Only advance the machine once content has actually rendered. A failed load
      // must not be indistinguishable from success (F3), so SESSION_LOADED is sent
      // here, not in a finally.
      this.sessionActor?.send({ type: 'SESSION_LOADED' });
    } catch (error) {
      Logger.error('[OSMDExerciseScene] loadRun failed; machine stays in loading:', error);
    }
  }

  /**
   * Build the OSMD payload from the expander's MusicXML + a client-built Phrase. A scale run
   * is unmetered: the transport counts one quarter-note beat per hidden measure, and no time
   * signature is drawn.
   */
  private toPhrasePayload(musicXml: string): OSMDPhraseDataEventPayload {
    const [numerator, denominator] = RUN_METER;
    const uuid = uuidv4();
    const cursorData = judgedNotesToCursorData(this.judgedNotes, RUN_METER);
    const phraseRaw = new Phrase(
      uuid,
      new MusicXML(musicXml, [new TimeSignature(numerator, denominator)]),
      this.judgedNotes.length,
      '',
      '',
      cursorData,
      cursorData,
      [],
    );
    return {
      phraseRaw,
      phrase: {
        musicXml,
        uuid,
        startTimestamp: 0,
        timeSignature: { numerator, denominator },
        showClef: true,
        showBrace: true,
        showTimeSig: false,
      },
    };
  }

  /**
   * The per-take grader: the take is graded through the grading engine as it is
   * recorded (Take Ladder: any red fails; zero red with more green than yellow is
   * clean; all green is flawless). The verdict the sitting counts, the band counts
   * shown, and the stored take record are one computation, so a replay of the record
   * rules the same verdict. `accuracy` is a derived readout only; the gate is the
   * composition, not the number.
   *
   * A take with no key pressed is flagged `noInput`: the scene routes it to the
   * non-scoring path (it neither clears nor breaks a streak), and the signal reads "no
   * input recorded" instead of a misleading all-red gradient.
   *
   * The tally is read from the rendered notes as the shared stream resolved them (what the
   * player sees), once every window has closed; the Take Ladder then rules the verdict. The
   * take is recorded beside it; a record failure is reported and never changes the grade.
   */
  private computeGrade(): void {
    // With no take open (nothing recorded the keys), the notes alone say whether anything was played.
    const pressed = this.openTake ? anyKeyPressed(this.openTake.events) : true;
    this.closeTakeRecord('finished');
    const grade = this.gradeFromNoteStates(pressed);
    this.lastGrade = grade;
    // MUS-319: a take with rendered notes but no played input is a no-op, not a
    // fail. Route it to the non-scoring GRADE_NO_INPUT event so an empty submit can
    // never break a mastery streak; the signal still reads "no input recorded".
    if (grade.noInput) {
      queueMicrotask(() => this.sessionActor?.send({ type: 'GRADE_NO_INPUT' }));
      return;
    }
    const { accuracy, verdict } = grade;
    // Deferred to avoid a re-entrant send during the evaluating transition.
    queueMicrotask(() => this.sessionActor?.send({ type: 'GRADE_RESULT', accuracy, verdict }));
  }

  /**
   * The rendered notes' resolved band states, graded by the Take Ladder's band rule. A take
   * with no key pressed is flagged no-input (it never counts).
   */
  private gradeFromNoteStates(pressed: boolean): GradeComputation {
    const states = this.phrases
      .flatMap((phrase) => phrase.getStaveNoteContainers())
      .map((container) => container.getState());
    const grade = gradeNoteStates(states);
    return { ...grade, noInput: grade.noInput || !pressed };
  }

  /**
   * The visible signal: a minimal, honest gradient + the resulting
   * cell-status language - never a PASS / FAIL stamp. Rendered as plain in-scene text
   * so it does not depend on the real app shell (MUS-481); the bespoke grading HUD is
   * MUS-482 (Phase 3) and is deliberately NOT built here.
   */
  private renderGradeSignal(context: ExerciseSessionContext): void {
    const grade = this.lastGrade;
    if (!grade) return;

    // This runs as a machine-commanded side effect (showPass/FailFeedback). The
    // visible signal is informational, so a render failure must never abort the
    // session machine - swallow and log rather than propagate into the transition.
    try {
      const message = grade.noInput
        ? 'No input recorded - take not graded'
        : `${grade.accuracy}% accurate  -  green ${grade.breakdown.green} / ` +
          `yellow ${grade.breakdown.yellow} / red ${grade.breakdown.red}\n${cellStatusLine(context)}`;
      this.drawGradeSignal(message);
    } catch (error) {
      Logger.error('[OSMDExerciseScene] grade-signal render failed:', error);
    }
  }

  /** The signal for a take left early: it was not graded and does not count. */
  private drawStoppedSignal(): void {
    try {
      this.drawGradeSignal('Take stopped - not graded');
    } catch (error) {
      Logger.error('[OSMDExerciseScene] grade-signal render failed:', error);
    }
  }

  /** Draw or update the single reused signal text object (top-left, fixed to camera). */
  private drawGradeSignal(message: string): void {
    if (this.gradeSignalText) {
      this.gradeSignalText.setText(message);
      return;
    }
    this.gradeSignalText = this.add
      .text(16, 16, message, {
        fontFamily: 'sans-serif',
        fontSize: '16px',
        color: '#e8e8e8',
        backgroundColor: '#00000080',
        padding: { x: 8, y: 6 },
      })
      .setScrollFactor(0)
      .setDepth(1000);
  }
}

/** A press's velocity as the record holds it: a whole MIDI velocity, 0-127 (64 if unknown). */
export function recordableVelocity(velocity: number): number {
  if (!Number.isFinite(velocity)) return 64;
  const midi =
    velocity > 0 && velocity <= 1 && !Number.isInteger(velocity) ? velocity * 127 : velocity;
  return Math.min(127, Math.max(0, Math.round(midi)));
}

/**
 * A take record that could not be written is reported (not only logged), with its reason, so
 * a missing record is visible; the take is still graded and still counts as played.
 */
function reportTakeRecordFailure(what: string, error: unknown): void {
  const reason = error instanceof Error ? error.message : String(error);
  Logger.error(`[OSMDExerciseScene] the take record ${what}:`, error);
  reportError(new Error(`exercise take record ${what}: ${reason}`), 'exercise.take-record');
}

/**
 * The user-facing cell status (Untouched -> Attempted -> Cleared -> Mastered)
 * for the current cell, read from the given session context. Callers pass the
 * post-grade context the machine hands its feedback actions (see showPass/FailFeedback)
 * so the line reflects THIS take. We CONSUME the machine's clearance / mastery policy
 * result rather than re-deriving the ladder. Never a pass/fail.
 */
export function cellStatusLine(context: ExerciseSessionContext): string {
  if (context.currentCell && context.currentConfig) {
    const earned = context.earned[takeKey(context.currentCell, context.currentConfig)];
    if (earned?.mastered) return 'Mastered';
    if (earned?.cleared) return 'Cleared';
  }
  return 'Not yet cleared';
}
